import React, { useEffect, useState } from "react";
import {
  FacebookFilled,
  TwitterSquareFilled,
  InstagramOutlined,
  PinterestFilled,
  TikTokFilled,
  YoutubeFilled,
  SearchOutlined,
  MenuOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Drawer } from "antd";
import SearchPopup from "../../SearchPopUp/SearchPopup";
import "./Header.css";
import Logo from "../../../Assets/logo/logo.png";

interface HeaderProps {
  onSearchClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onSearchClick }) => {
  const [prevScroll, setPrevScroll] = useState(0);
  const [showTopBar, setShowTopBar] = useState(true);
  const [navFixed, setNavFixed] = useState(false);
  const [isSearchPopupOpen, setIsSearchPopupOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (currentScroll > prevScroll && currentScroll > 60) {
        setShowTopBar(false);
      } else {
        setShowTopBar(true);
      }

      const headerHeight =
        document.querySelector(".header-top")?.clientHeight || 100;
      if (currentScroll > headerHeight + 60) {
        setNavFixed(true);
      } else {
        setNavFixed(false);
      }

      setPrevScroll(currentScroll);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScroll]);

  const isActive = (path: string) =>
    location.pathname === path ? "active" : "";

  const handleSearchClick = () => {
    setIsSearchPopupOpen(true);
    if (onSearchClick) onSearchClick();
  };

  const handleSearch = (query: string) => {
    navigate(`/search?s=${encodeURIComponent(query)}`);
  };

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  return (
    <header className="header">
      {/* Top Bar */}
      <div className={`header-top ${showTopBar ? "visible" : "hidden"}`}>
        <div className="container">
          <div className="brand">
            <Link to="/" className="Brand">
              <img src={Logo} alt="Lifestyle Online" />
            </Link>
          </div>
          <div className="social">
            <a href="#" aria-label="Facebook" className="social-link">
              <FacebookFilled />
            </a>
            <a href="#" aria-label="Twitter" className="social-link">
              <TwitterSquareFilled />
            </a>
            <a href="#" aria-label="Instagram" className="social-link">
              <InstagramOutlined />
            </a>
            <a href="#" aria-label="Pinterest" className="social-link">
              <PinterestFilled />
            </a>
            <a href="#" aria-label="TikTok" className="social-link">
              <TikTokFilled />
            </a>
            <a href="#" aria-label="YouTube" className="social-link">
              <YoutubeFilled />
            </a>
          </div>
        </div>
      </div>

      {/* Nav Bar */}
      <nav className={`header-nav ${navFixed ? "fixed-nav" : ""}`}>
        <div className="container">
          <ul className="menu">
            <li>
              <Link to="/" className={`menu-link ${isActive("/")}`}>
                Home
              </Link>
            </li>
            <li>
              <Link to="/details" className={`menu-link ${isActive("/details")}`}>
                Details
              </Link>
            </li>
            <li>
              <Link to="/contact" className={`menu-link ${isActive("/contact")}`}>
                Contact
              </Link>
            </li>
          </ul>

          <div className="nav-actions">
            {/* Search button */}
            <button
              className="icon-btn search"
              aria-label="Search"
              onClick={handleSearchClick}
            >
              <SearchOutlined />
            </button>

            {/* Mobile Menu Button - same CSS class for identical look */}
            <button
              className="icon-btn menu"
              onClick={openDrawer}
              aria-label="Menu"
            >
              <MenuOutlined />
            </button>
          </div>
        </div>
      </nav>

      {/* Drawer for Mobile Menu */}
      <Drawer
        placement="right"
        onClose={closeDrawer}
        open={isDrawerOpen}
        closeIcon={<CloseOutlined />}
        styles={{
          header: { display: "none" },
          body: {
            padding: "40px 30px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          },
        }}
      >
        <div>
          <h2 style={{ color: "#1e293b", fontWeight: 700 }}>
            Lifestyle Online<span style={{ color: "#f87171" }}>.</span>
          </h2>

          <ul style={{ listStyle: "none", padding: "20px 0", margin: 0 }}>
            <li style={{ marginBottom: "20px" }}>
              <Link
                to="/"
                onClick={closeDrawer}
                style={{
                  textDecoration: "none",
                  color: "#0f172a",
                  fontSize: "18px",
                  fontWeight: 500,
                }}
              >
                Home
              </Link>
            </li>
            <li style={{ marginBottom: "20px" }}>
              <Link
                to="/details"
                onClick={closeDrawer}
                style={{
                  textDecoration: "none",
                  color: "#0f172a",
                  fontSize: "18px",
                  fontWeight: 500,
                }}
              >
                Details
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                onClick={closeDrawer}
                style={{
                  textDecoration: "none",
                  color: "#0f172a",
                  fontSize: "18px",
                  fontWeight: 500,
                }}
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>

        <div style={{ display: "flex", gap: "15px" }}>
          <i className="fab fa-facebook-f" style={{ fontSize: "20px" }}></i>
          <i className="fab fa-twitter" style={{ fontSize: "20px" }}></i>
          <i className="fab fa-instagram" style={{ fontSize: "20px" }}></i>
        </div>
      </Drawer>

      {/* Search Popup */}
      <SearchPopup
        isOpen={isSearchPopupOpen}
        onClose={() => setIsSearchPopupOpen(false)}
        onSearch={handleSearch}
      />
    </header>
  );
};

export default Header;
